import React, { useState, useEffect } from 'react';
import {
    Manager,
    EntryPoint,
    MainHeader,
    ContextProvider,
    RootContainer,
    MessagingCanvas,
    MessageList,
    MessageBubble as MessageBubbleTwilio,
    MessageInput,
} from '@twilio/flex-webchat-ui';
import { Config } from './Config/Config';
import ImageVacasa from '../../assets/images/vacasa-logo-blue.svg';
import { IconCloseChat, IconOpenChat } from '../../assets/icons/index';
import { defaultTimeZone, TranslationUtils, ValidationUtils } from '../../utils';
import { ConfigProps, FlexChatProps, ManagerState } from '../../types/webChat.type';
import { ContainerMessage, ChatLoading, MessageBubble } from './Template/Template';
import moment from 'moment-timezone';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { TripState } from '../../types/trip.store.type';
import { getChatSchedule, getExtraData, tracking } from './WebChatHelper';
import { UserState } from '../../types/user.store.type';
import { StoreState } from '../../types/store.type';

const defaultManagerState = {
    loading: false,
    manager: undefined,
    error: undefined,
    log: true,
};

export const WebChat: React.FC<FlexChatProps> = ({ name, extras, isDisabled }) => {
    const trip = useSelector((store: { trip: TripState }) => store.trip);
    const user: UserState = useSelector((store: StoreState) => store.user);
    const now = moment().tz(defaultTimeZone);
    const [managerState, setManagerState] = useState<ManagerState>(defaultManagerState);
    const { manager, loading, error, log } = managerState;
    const chatMessage: string = TranslationUtils.get('chat_default_message');
    const chatDefaultName: string = TranslationUtils.get('chat_default_name');
    const chatMessageButton: string = TranslationUtils.get('chat_start_new_button');
    const chatEndText: string = TranslationUtils.get('chat_start_content');

    const reservationId = trip.reservation?.id || '';

    let flexAccountSid: string = '';
    let flexFlowSid: string = '';

    try {
        flexAccountSid = ValidationUtils.validateAndGetEnvVar('REACT_APP_ACCOUNT_SID');
        flexFlowSid = ValidationUtils.validateAndGetEnvVar('REACT_APP_FLOW_SID');
    } catch (e) {
        isDisabled = true;
    }
    const location = useLocation();
    const chatSchedule = getChatSchedule(now, defaultTimeZone);

    useEffect(() => {
        const { guests, stay, reservation, unit, access, home_info } = trip;

        if (guests && stay && reservation && access && unit && home_info) {
            extras = { ...getExtraData(now, trip.isCancelled, stay, reservation, unit, guests, access, home_info, user) };
        }

        const config: ConfigProps = {
            flexAccountSid,
            flexFlowSid,
            user: {
                friendlyName: name,
                ...extras,
            },
        };

        if (config.flexFlowSid && config.flexAccountSid && config.user) {
            // If Twilio Chat Manager is initialized update config
            if (manager) {
                manager.updateConfig(Config(config, isDisabled));
                return;
            }

            setManagerState({
                ...defaultManagerState,
                loading: true,
            });

            // Build chat config
            const chatConfig = Config(config, isDisabled);

            // Vacasa settings
            EntryPoint.defaultProps.tagline = '';
            MainHeader.defaultProps.imageUrl = ImageVacasa;
            MainHeader.defaultProps.titleText = '';

            // Change icon when start the chat
            EntryPoint.defaultProps.iconClosed = chatSchedule.isEnable ? <IconOpenChat /> : <IconCloseChat />;

            //Remove default Content and add new custom Content
            MessageList.WelcomeMessage.Content.remove('icon');
            MessageList.WelcomeMessage.Content.remove('text');
            MessageList.WelcomeMessage.Content.add(
                <ContainerMessage
                    key="content"
                    isOpen={chatSchedule.isEnable}
                    availableTimes={{
                        localTimeToOpen: chatSchedule.localTimeToOpen,
                        localTimeToClose: chatSchedule.localTimeToClose,
                    }}
                />,
                { sortOrder: 0 }
            );

            //Remove default Content
            MessageBubbleTwilio.Content.remove('body');
            MessageBubbleTwilio.Content.remove('header');

            if (!chatSchedule.isEnable) {
                // Remove the input component
                MessageInput.Content.remove('textarea');
            } else {
                //add new custom Content
                MessageBubbleTwilio.Content.add(<MessageBubble key={'MessageBubble'} />, { sortOrder: 0 });

                // Predefined message for the user
                MessagingCanvas.defaultProps.predefinedMessage = {
                    body: chatMessage,
                    authorName: chatDefaultName,
                    isFromMe: false,
                };
            }

            // Start init of the Twilio Chat Manager with custom chatConfig
            Manager.create(chatConfig)
                .then((manager) => {
                    manager.strings.MessageCanvasTrayContent = chatEndText;
                    manager.strings.MessageCanvasTrayButton = chatMessageButton;
                    manager.strings.TypingIndicator = 'Typing...';
                    setManagerState({
                        ...defaultManagerState,
                        manager,
                    });
                })
                .catch((error) => {
                    setManagerState({
                        ...defaultManagerState,
                        error: error.message,
                    });
                });

            if (!isDisabled) {
                tracking(chatSchedule.isEnable, 'show', location, reservationId);
            }
        }
    }, []);

    const handleTrackingClick = () => {
        if (manager && !error && !loading && log) {
            tracking(chatSchedule.isEnable, 'click', location, reservationId);
            setManagerState({
                ...managerState,
                log: false,
            });
        }
    };

    return (
        <div
            onClick={() => {
                handleTrackingClick();
            }}
        >
            {error && <ChatLoading text={TranslationUtils.get('chat_error')} />}
            {loading && <ChatLoading text={TranslationUtils.get('chat_loading')} />}
            {manager && !error && !loading && (
                <ContextProvider manager={manager}>
                    <RootContainer />
                </ContextProvider>
            )}
        </div>
    );
};
